<template>
	<MoleculeHeaderSubBar
		v-if="!isSalesHeader"
		:layer="LayerEnum.Fysio"
		:menu-social-items
		:menu-service-items
	/>
	<div
		:class="[
			'sticky top-0 z-20 transition-transform duration-200',
			isScrollingDown ? '-translate-y-full' : 'translate-y-0',
		]"
		:data-testid="isScrollingDown ? 'header-hidden' : 'header-visible'"
	>
		<header class="bg-gradient-to-r from-purple-700 to-purple-900">
			<div
				class="container flex items-center flex-wrap gap-x-6 gap-y-8 lg:gap-x-8 py-8 lg:py-0 min-h-32"
			>
				<button
					v-if="menuItems"
					class="lg:hidden z-10 w-10 flex flex-col mr-12"
					@click="clickMenuButton"
				>
					<span class="sr-only">Menu openen</span>
					<AtomFontAwesome
						icon="fa-bars"
						class="size-10 text-white"
					/>
				</button>
				<div
					class="flex items-center justify-center relative"
				>
					<AtomLink
						v-bind="homeLink"
						class="lg:h-28 flex items-center"
					>
						<AtomLogoFysio class="h-14" />
					</AtomLink>
				</div>
				<LazyOrganismMainMenu
					v-if="!isSalesHeader && menuItems"
					class="hidden lg:flex xl:ml-12 text-white order-last xl:order-none basis-full xl:basis-auto rounded-full -mx-4"
					:menu-items="menuItems"
				/>
				<div
					class="flex items-center justify-end basis-full lg:basis-auto lg:grow gap-8 lg:gap-8"
				>
					<div
						v-if="isSalesHeader"
						class="flex items-center gap-4 text-purple-200"
					>
						<AtomFontAwesome
							icon="fa-location-dot"
							class="w-8"
						/>
						<span>
							{{ streetName }} {{ streetNumber }} - {{ city }}
						</span>
					</div>
					<AtomButton
						class="cursor:hidden"
						:to="phoneLink"
						size="sm"
						:variant="ButtonVariant.LightBorder"
						icon-left="fa-phone"
						text="Bel ons"
						@click="trackClickToCall('OrganismHeaderBar_Call_Mobile')"
					/>
					<AtomButton
						class="touch:hidden text-white"
						:to="phoneLink"
						size="md"
						:variant="ButtonVariant.Transparent"
						icon-left="fa-phone"
						:text="phoneNumber"
						@click="trackClickToCall('OrganismHeaderBar_Call_Desktop')"
					/>
					<AtomButton
						v-if="!isSalesHeader"
						class="lg:hidden"
						size="sm"
						:variant="ButtonVariant.Yellow"
						icon-left="fa-calendar-days"
						text="Afspraak maken"
						data-testid="OrganismHeaderBar-appointment-mobile"
						@click="
							() => {
								trackButtonClick('OrganismHeaderBar_CTA_FysioAppointment_Mobile');
								clickAppointmentButton();
							}
						"
					/>
					<AtomButton
						v-if="!isSalesHeader"
						class="hidden lg:flex"
						size="md"
						:variant="ButtonVariant.Yellow"
						icon-left="fa-calendar-days"
						text="Afspraak maken"
						data-testid="OrganismHeaderBar-appointment-desktop"
						@click="
							() => {
								trackButtonClick('OrganismHeaderBar_CTA_FysioAppointment_Desktop');
								clickAppointmentButton();
							}
						"
					/>
				</div>
			</div>
		</header>
	</div>
</template>

<script lang="ts" setup>
import { city, streetName, streetNumber } from '#base/core/consts/companyData';
import { ButtonVariant } from '#base/core/enums/ButtonVariantEnum';
import type { SubHeaderMenuItemType } from '#base/core/types/SubHeaderMenuItemType';
import type { UrlType } from '#base/core/types/UrlType';
import { LayerEnum } from '#base/core/enums/LayerEnum';
import type { MainMenuItemType } from '#base/core/types/MainMenuItemType';

defineProps<{
	menuSocialItems?: SubHeaderMenuItemType[];
	menuServiceItems?: SubHeaderMenuItemType[];
	menuItems?: MainMenuItemType[];
	phoneNumber: string;
	phoneLink: UrlType;
	homeLink: UrlType;
	isSalesHeader?: boolean;
}>();

const { isScrollingDown } = useScrollDirection();
const { trackClickToCall, trackButtonClick } = useEventTracking();

const emit = defineEmits(['clickMenuButton', 'clickAppointmentButton']);

const clickMenuButton = () => emit('clickMenuButton');
const clickAppointmentButton = () => emit('clickAppointmentButton');
</script>
